import { useEffect, useState, useCallback } from "react";
import { Zap, AlertTriangle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";

/**
 * Banner informativo posizionato all'interno dei popup di upload che
 * consumano quote (fatture OCR, import menu PDF, generazione ricetta AI).
 * Mostra il numero di upload rimanenti questo mese (piano + extra).
 *
 * Si aggiorna automaticamente all'evento `renova:quota-refresh`.
 */
export function UploadRemainingBanner({ kind, label }) {
    // kind: "invoices" | "menus" | "recipes"
    const [quota, setQuota] = useState(null);
    const navigate = useNavigate();

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/billing/quota");
            setQuota(data);
        } catch {
            setQuota(null);
        }
    }, []);

    useEffect(() => {
        load();
        const h = () => load();
        window.addEventListener("renova:quota-refresh", h);
        return () => window.removeEventListener("renova:quota-refresh", h);
    }, [load]);

    if (!quota) return null;

    const planLeft = Math.max(
        0,
        (quota[`plan_${kind}`] || 0) - (quota[`${kind}_used`] || 0)
    );
    const extra = quota[`extra_${kind}`] || 0;
    const planTotal = quota[`plan_${kind}`] || 0;
    const planUsed = quota[`${kind}_used`] || 0;
    const total = planLeft + extra;
    const exhausted = total === 0;
    const low = !exhausted && total <= 2;
    const planTierLabel = (quota.tier_for_quota || "base").toUpperCase();

    return (
        <div
            data-testid={`upload-banner-${kind}`}
            className={
                "rounded-xl border px-4 py-3 flex items-center gap-3 " +
                (exhausted
                    ? "bg-red-50 border-red-200"
                    : low
                    ? "bg-amber-50 border-amber-300"
                    : "bg-brand-cream border-brand-line")
            }
        >
            <div
                className={
                    "h-9 w-9 rounded-full flex items-center justify-center shrink-0 " +
                    (exhausted
                        ? "bg-red-100 text-brand-danger"
                        : low
                        ? "bg-amber-100 text-brand-warning"
                        : "bg-brand-gold/15 text-brand-gold")
                }
            >
                {exhausted ? <AlertTriangle size={16} /> : <Zap size={16} />}
            </div>
            <div className="flex-1 min-w-0">
                {exhausted ? (
                    <>
                        <div className="text-[13px] font-bold text-brand-danger">
                            {label}: quota esaurita questo mese
                        </div>
                        <div className="text-[11px] text-brand-ink/70">
                            Hai usato tutti gli {planTotal} {label.toLowerCase()} del piano{" "}
                            {planTierLabel}. Acquista la ricarica da 10€ per continuare.
                        </div>
                    </>
                ) : (
                    <>
                        <div className="text-[13px] font-semibold text-brand-ink">
                            {label} rimanenti questo mese:{" "}
                            <span className="font-display text-base text-brand-navy">
                                {total}
                            </span>
                            {extra > 0 && (
                                <span className="ml-1.5 text-[11px] text-brand-gold font-bold">
                                    ({planLeft} piano + {extra} extra)
                                </span>
                            )}
                        </div>
                        <div className="text-[11px] text-brand-muted mt-0.5">
                            Piano {planTierLabel}: {planUsed}/{planTotal} usati · periodo{" "}
                            {quota.period}
                        </div>
                    </>
                )}
            </div>
            <button
                type="button"
                onClick={() => navigate("/billing")}
                data-testid={`upload-banner-${kind}-topup`}
                className={
                    "text-[11px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-full transition shrink-0 " +
                    (exhausted
                        ? "bg-brand-gold text-brand-navy hover:bg-brand-goldDark shadow-gold"
                        : "border border-brand-line bg-white text-brand-ink hover:border-brand-gold/50")
                }
            >
                {exhausted ? "Carica ora" : "Gestisci"}
            </button>
        </div>
    );
}
